import React, { useState, useEffect, useRef, useCallback,} from "react";
import { NavLink, Link, useLocation,} from "react-router-dom";
import { MenuOutlined, CloseOutlined, ArrowRightOutlined, DownOutlined, } from "@ant-design/icons";

import logo from "../../Asset/Yashavi.png";
import "./Navbar.css";

/* =========================================
   PRODUCTS
========================================= */
const PRODUCTS = [
  { name: "Transport Management System", path: "/TransportManagement" },
  { name: "School Management System", path: "/School" },
  { name: "Smart Inventory Management", path: "/invoice-balancing" },
  { name: "E-commerce Solutions", path: "/Ecommerce" },
  { name: "Clinic Management System", path: "/Clinic" },
  { name: "Lead Management Software", path: "/lead" },
  { name: "QR Attendance System", path: "/QRAttendance" },
];

/* =========================================
   NAVIGATION
========================================= */
const NAV = [
  { path: "/", label: "Home" },
  { path: "/about", label: "About" },
  { path: "/services", label: "Services" },
  { path: "/Products", label: "Products", hasDropdown: true },
  { path: "/portfolio", label: "Portfolio" },
  { path: "/contact", label: "Contact" },
];


export default function Navbar() {
  /* =========================================
     STATES
  ========================================= */
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false); 

  /* =========================================
     REFS
  ========================================= */
  const navRef = useRef(null);
  const linkRefs = useRef({});

  /* =========================================
     LOCATION
  ========================================= */
  const location = useLocation();

  /* =========================================
     ACTIVE INDICATOR
  ========================================= */
  const [indicator, setIndicator] = useState({
    left: 0,
    width: 0,
    ready: false,
  });

  /* =========================================
     SCROLL EFFECT
  ========================================= */
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 50 || location.pathname !== "/");
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  /* =========================================
     LOCATION CHANGE
  ========================================= */
  useEffect(() => {
    setOpen(false);
    setProductsOpen(false); 
    setMobileProductsOpen(false); 
  }, [location.pathname]);

  /* =========================================
     ACTIVE NAV INDICATOR
  ========================================= */
  const updateIndicator = useCallback(() => {
    const active = NAV.find((item) => {
      if (item.path === "/") return location.pathname === "/";
      if (item.hasDropdown) {
        return (
          location.pathname === "/Products" ||
          PRODUCTS.some((product) => location.pathname === product.path)
        );
      }
      return location.pathname.startsWith(item.path);
    });

    const el = active ? linkRefs.current[active.path] : null;
    if (el && navRef.current) {
      const navRect = navRef.current.getBoundingClientRect();
      const linkRect = el.getBoundingClientRect();
      setIndicator({
        left: linkRect.left - navRect.left,
        width: linkRect.width,
        ready: true,
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    updateIndicator();
    window.addEventListener("resize", updateIndicator);
    return () => window.removeEventListener("resize", updateIndicator);
  }, [updateIndicator]);

  /* =========================================
     HANDLERS
  ========================================= */
  const handleProductsClick = (e) => {
    e.preventDefault();
    setProductsOpen((prev) => !prev);
  };

  const handleNormalNavClick = () => {
    setProductsOpen(false);
  };

  return (
    <>
      {/* =====================================
          DESKTOP NAVBAR
      ===================================== */}
      <header className={`floating-nav ${scrolled ? "nav-scrolled" : ""}`}>
        <div className="nav-capsule">
          <Link to="/" className="logo" onClick={() => setProductsOpen(false)}>
            <img src={logo} alt="YashviTech" className="logo-img" />
          </Link>

          <nav className="nav-links-wrap" ref={navRef}>
            {indicator.ready && (
              <span
                className="nav-indicator"
                style={{ left: indicator.left, width: indicator.width }}
              />
            )}

            {NAV.map((item) => {
              const isProducts = item.hasDropdown;
              return (
                <div
                  className={`nav-link-wrapper ${isProducts ? "has-products-dropdown" : ""}`}
                  key={item.path}
                  ref={(el) => { linkRefs.current[item.path] = el; }}
                >
                  <NavLink
                    end={item.path === "/"}
                    to={item.path}
                    onClick={isProducts ? handleProductsClick : handleNormalNavClick}
                    className={({ isActive }) => {
                      const productActive =
                        isProducts &&
                        (location.pathname === "/Products" ||
                          PRODUCTS.some((p) => location.pathname === p.path));
                      return isActive || productActive || (isProducts && productsOpen)
                        ? "nav-link-item active"
                        : "nav-link-item";
                    }}
                  >
                    {item.label}
                  </NavLink>

                  {isProducts && productsOpen && (
                    <div className="products-dropdown">
                      {PRODUCTS.map((product) => (
                        <Link
                          key={product.path}
                          to={product.path}
                          onClick={() => setProductsOpen(false)}
                          className={`product-dropdown-item ${
                            location.pathname === product.path ? "selected" : ""
                          }`}
                        >
                          {product.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="nav-actions">
            <Link className="nav-cta-btn" to="/internship" onClick={() => setProductsOpen(false)}>
              <span>Training & Internship</span>
            </Link>
            <button
              className="nav-burger"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <CloseOutlined /> : <MenuOutlined />}
            </button>
          </div>
        </div>
      </header>

      {/* =====================================
          MOBILE MENU (UPDATED)
      ===================================== */}
      <div className={`mobile-overlay ${open ? "open" : ""}`}>
        <div className="mobile-overlay-bg" onClick={() => setOpen(false)} />

        <nav className="mobile-overlay-nav">
          {NAV.map((item, index) => {
            if (item.hasDropdown) {
              return (
                <div key={item.path} className="mobile-dropdown-wrapper">
                  <div 
                    className={`mobile-overlay-link dropdown-toggle ${mobileProductsOpen ? "active" : ""}`}
                    onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span className="mol-num">0{index + 1}</span>
                      {item.label}
                    </div>
                    <DownOutlined className={`mobile-dropdown-icon ${mobileProductsOpen ? 'rotate' : ''}`} />
                  </div>
                  
                  {/* MOBILE PRODUCTS SUBMENU */}
                  <div className={`mobile-submenu ${mobileProductsOpen ? "open" : ""}`}>
                    {PRODUCTS.map((prod) => (
                      <NavLink
                        key={prod.path}
                        to={prod.path}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) => 
                          isActive ? "mobile-submenu-link active" : "mobile-submenu-link"
                        }
                      >
                        {prod.name}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            }

            // Normal mobile link
            return (
              <NavLink
                key={item.path}
                end={item.path === "/"}
                to={item.path}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  isActive ? "mobile-overlay-link active" : "mobile-overlay-link"
                }
              >
                <span className="mol-num">0{index + 1}</span>
                {item.label}
              </NavLink>
            );
          })}

          <Link to="/internship" className="mobile-overlay-cta" onClick={() => setOpen(false)}>
            Training & Internship
            <ArrowRightOutlined />
          </Link>
        </nav>
      </div>
    </>
  );
}